import { createFileRoute, Link } from "@tanstack/react-router";
import clsx from "clsx";
import { twMerge } from "tailwind-merge";

import { useIsLoggedIn } from "@/auth/shared";
import { button } from "@/components/base/button";
import { title } from "@/components/base/primitives";
import { VenuesList } from "@/components/venues/list";
import { DefaultLayout } from "@/layouts/default";

const BRAND_ANCHOR_CLASS =
  "max-h-[85px] flex-1 flex items-center justify-center";

const BRAND_IMG_CLASS = "max-w-full max-h-full";

export const Route = createFileRoute("/")({
  component: RouteComponent,
});

function RouteComponent() {
  const isLoggedIn = useIsLoggedIn();

  return (
    <DefaultLayout classNames={{ content: "bg-content-background" }}>
      <div className="relative h-[75svh]">
        <div className="h-full overflow-hidden">
          <img
            src="/homepage.jpg"
            alt="CBVA Home Page"
            className="h-full w-full object-cover"
            style={{ objectPosition: "50% 65%" }}
          />
        </div>
        <Link
          to={isLoggedIn ? "/tournaments" : "/sign-up"}
          className={button({
            class:
              "absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 font-bold whitespace-nowrap text-2xl sm:text-4xl",
            color: "primary",
            radius: "full",
            size: "4xl",
          })}
        >
          {isLoggedIn ? "Events" : "Sign Up"}
        </Link>
      </div>
      <div className="flex w-full flex-col items-center justify-center space-y-6 pt-10 pb-6 md:flex-row md:space-y-0">
        <div className="flex w-full flex-[3] flex-row">
          <a
            className={BRAND_ANCHOR_CLASS}
            href="https://www.michelobultra.com/"
            target="_blank"
            rel="noopener"
          >
            <img
              className={BRAND_IMG_CLASS}
              src="/brands/mkultra.svg"
              alt="Michelob Ultra"
            />
          </a>
          <a
            className={BRAND_ANCHOR_CLASS}
            href="https://www.cedars-sinai.org/programs/ortho/specialties/sports-medicine.html"
            target="_blank"
            rel="noopener"
          >
            <img
              className={BRAND_IMG_CLASS}
              src="/brands/cedars-sinai/stacked.svg"
              alt="Cedars Sinai Sports Medicine"
            />
          </a>
          <a
            className={BRAND_ANCHOR_CLASS}
            href="https://www.toyota.com/"
            target="_blank"
            rel="noopener"
          >
            <img
              className={BRAND_IMG_CLASS}
              src="/brands/toyota.svg"
              alt="Toyota"
            />
          </a>
        </div>
        <div className="flex w-full flex-[2] flex-row">
          <a
            className={BRAND_ANCHOR_CLASS}
            href="https://www.laorbit.com/"
            target="_blank"
            rel="noopener"
          >
            <img
              // Orange raster logo → flatten to black then lift to #808080
              // so it matches the gray of the other (gray-fill) brand logos.
              // ~5.4:1 wordmark: width-capped so its cap height lands near
              // Cedars' rather than letting max-h blow it up across the row.
              className={twMerge(
                BRAND_IMG_CLASS,
                "[filter:brightness(0)_invert(0.5)]",
                "w-full max-w-[220px]",
              )}
              src="/brands/la-orbit.png"
              alt="LA Orbit"
            />
          </a>
          <a
            className={BRAND_ANCHOR_CLASS}
            href="https://www.wilson.com/en-us/volleyball"
            target="_blank"
            rel="noopener"
          >
            <img
              className={twMerge(BRAND_IMG_CLASS, "scale-75")}
              src="/brands/wilson.svg"
              alt="Wilson"
            />
          </a>
        </div>
      </div>
      <div className="bg-navbar-background text-navbar-foreground relative h-[75svh] w-full text-center">
        <img
          alt="A bear"
          src="/bear.svg"
          className="absolute top-1/2 left-0 z-0 h-1/4 -translate-y-1/2 md:h-auto"
        />
        <img
          alt="A bear"
          src="/bear.svg"
          className="absolute top-1/2 right-0 z-0 h-1/4 -translate-y-1/2 -scale-x-100 md:h-auto"
        />

        <div className="relative z-1 mx-auto flex h-full max-w-2xl flex-col items-center justify-center space-y-6 px-3">
          <h1 className={title({ size: "md", class: "leading-tight" })}>
            The best of the best win on California sand.
          </h1>
          <h2 className={title({ size: "lg" })}>Join them. Beat them.</h2>
          <Link
            to={isLoggedIn ? "/tournaments" : "/sign-up"}
            className={button({
              color: "primary",
              radius: "full",
              size: "lg",
              className: "font-bold",
            })}
          >
            Start Here
          </Link>
        </div>
      </div>
      <LiveScoringSection isLoggedIn={isLoggedIn} />
      <div className="relative flex h-[90svh] w-full flex-col items-center justify-center text-start">
        <img
          loading="lazy"
          src="/premier_bg.jpg"
          className="h-full w-full object-cover"
          alt="View of a tournament from above"
        />
        <div className="absolute top-0 left-0 flex h-full w-full flex-col items-center justify-center text-center">
          <div className="flex h-full max-w-3xl flex-col items-center justify-center space-y-3">
            <div className="relative w-[10svw] md:w-[64]">
              <img
                loading="lazy"
                src="/star.svg"
                className="absolute bottom-1/2 left-1/2 w-[10svw] -translate-x-1/2 -translate-y-8 transform md:w-[64]"
                alt="Star"
              />
            </div>
            <h3
              className={title({ size: "xs", class: "font-bold text-white" })}
            >
              The CBVA is California's premier tour
            </h3>
            <p className="mx-6 my-2 max-w-[900] pb-[100] text-[5svw] leading-snug font-light text-white md:text-4xl md:leading-snug">
              Founded in 1962, the CBVA was created to bring together all levels
              of beach volleyball players to compete in high-quality tournaments
              along the California coast.
            </p>
          </div>
        </div>
      </div>
      <VenuesList />
    </DefaultLayout>
  );
}

function LiveScoringSection({ isLoggedIn }: { isLoggedIn: boolean }) {
  return (
    <div className="bg-content-background w-full px-4 py-16 md:py-24">
      <div className="mx-auto grid max-w-5xl grid-cols-1 gap-8 md:grid-cols-2 md:items-center">
        <div className="bg-content-background-alt flex flex-col gap-6 rounded-md p-8 md:p-12">
          <h2
            className={title({
              size: "sm",
              class: "text-navbar-background leading-tight",
            })}
          >
            ROOTING FOR THE HOME TEAM?
            <br />
            <span className="font-bold">FOLLOW ALONG WITH LIVE SCORING.</span>
          </h2>
          <p className="text-base leading-relaxed">
            Cheer on your favorite team with live scoring and live brackets. You
            don&apos;t have to miss any of the game day action this season.
          </p>
          <div>
            <Link
              to={isLoggedIn ? "/tournaments" : "/sign-up"}
              className={button({
                color: "primary",
                radius: "full",
                className: "font-bold",
              })}
            >
              LET&apos;S GO
            </Link>
          </div>
        </div>

        <ExampleMatchCard />
      </div>
    </div>
  );
}

function ExampleMatchCard() {
  return (
    <div className="flex flex-col gap-4">
      <div>
        <h3 className="text-base font-bold">Manhattan Beach Open</h3>
        <div className="mt-1 flex items-center gap-2 text-sm text-gray-600">
          <span>Semi-Finals</span>
          <span className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-600">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-red-500" />
            Live
          </span>
        </div>
      </div>

      <ExampleGame
        label="Match 1"
        currentSet={3}
        teams={[
          {
            seed: 1,
            players: ["Jane Williams", "Masha Schneider"],
            scores: [17, 22, 9],
          },
          {
            seed: 4,
            players: ["Joni Mitchell", "Cassandra Jones"],
            scores: [21, 20, 11],
          },
        ]}
      />

      <ExampleGame
        label="Match 2"
        currentSet={2}
        teams={[
          {
            seed: 6,
            players: ["Susan Boyle", "Bridgette Wilson"],
            scores: [11, 14, null],
          },
          {
            seed: 2,
            players: ["Kelly Clarkson", "Miranda Weiss"],
            scores: [21, 19, null],
          },
        ]}
      />
    </div>
  );
}

function ExampleGame({
  label,
  currentSet,
  teams,
}: {
  label: string;
  currentSet: 1 | 2 | 3;
  teams: Array<{
    seed: number;
    players: [string, string];
    scores: [number | null, number | null, number | null];
  }>;
}) {
  return (
    <div className="overflow-hidden rounded-md border border-gray-200 bg-white shadow-sm">
      <div className="bg-navbar-background text-navbar-foreground grid grid-cols-[1fr_3rem_3rem_3rem] items-center px-3 py-1.5 text-xs">
        <span className="font-semibold">{label}</span>
        {([1, 2, 3] as const).map((s) => (
          <span
            key={s}
            className={clsx(
              "text-center",
              s === currentSet ? "font-bold" : "text-gray-300",
            )}
          >
            Set {s}
          </span>
        ))}
      </div>
      {teams.map((team, idx) => (
        <div
          key={idx}
          className={clsx(
            "grid grid-cols-[1fr_3rem_3rem_3rem] items-center px-3 py-2 text-sm",
            idx === 0 && "border-b border-gray-100",
          )}
        >
          <div className="flex items-center gap-3">
            <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">
              #{team.seed}
            </span>
            <div className="flex flex-col leading-tight">
              <span className="font-semibold">{team.players[0]}</span>
              <span className="font-semibold">{team.players[1]}</span>
            </div>
          </div>
          {team.scores.map((score, sIdx) => {
            const setNum = (sIdx + 1) as 1 | 2 | 3;
            const isCurrent = setNum === currentSet;
            return (
              <span
                key={sIdx}
                className={clsx(
                  "text-center tabular-nums",
                  score == null
                    ? "text-gray-300"
                    : isCurrent
                      ? "text-xl font-bold"
                      : "text-lg text-gray-400",
                )}
              >
                {score ?? ""}
              </span>
            );
          })}
        </div>
      ))}
    </div>
  );
}
